// Frontend utility helpers for formatting dates/times and common UI patterns.
export const formatDate = (date: Date): string => {
  return date.toLocaleDateString();
};

/**
 * Format a timestamp as relative time (e.g. "a few seconds ago", "1 minute ago").
 * @param timestamp - Unix timestamp in ms, or numeric string
 * @returns Human-readable relative time string
 */
/**
 * Format a future (or past) epoch-seconds timestamp as a human-readable countdown.
 * - Future: "in 5 minutes", "in 2 hours", "in 1 day"
 * - Imminent: "starting now"
 * - Past (late): "5 minutes late", "2 hours late"
 * @param epochSeconds - Unix timestamp in seconds
 */
export const formatTimeUntil = (epochSeconds: number): string => {
  const start = Number(epochSeconds);
  if (!Number.isFinite(start) || start <= 0) return '';
  const diffSeconds = Math.round(start - Date.now() / 1000);
  const abs = Math.abs(diffSeconds);
  const minutes = Math.floor(abs / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (diffSeconds > 0) {
    if (diffSeconds < 60) return 'in less than a minute';
    if (minutes === 1) return 'in 1 minute';
    if (minutes < 60) return `in ${minutes} minutes`;
    if (hours === 1) return 'in 1 hour';
    if (hours < 24) return `in ${hours} hours`;
    if (days === 1) return 'in 1 day';
    return `in ${days} days`;
  } else {
    if (abs < 60) return 'starting now';
    if (minutes === 1) return '1 minute late';
    if (minutes < 60) return `${minutes} minutes late`;
    if (hours === 1) return '1 hour late';
    if (hours < 24) return `${hours} hours late`;
    if (days === 1) return '1 day late';
    return `${days} days late`;
  }
};

export const formatRelativeTime = (timestamp: string | number): string => {
  let ts: number;
  if (typeof timestamp === 'string') {
    const raw = timestamp.trim();
    // Numeric strings are treated as unix timestamps; ISO strings are parsed as dates.
    if (/^\d+(\.\d+)?$/.test(raw)) {
      ts = Number(raw);
    } else {
      ts = Date.parse(raw);
    }
  } else {
    ts = timestamp;
  }
  if (!Number.isFinite(ts) || ts <= 0) return '';
  // Accept both epoch seconds and epoch milliseconds.
  if (ts > 0 && ts < 1_000_000_000_000) {
    ts = ts * 1000;
  }
  const diff = Date.now() - ts;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);

  if (seconds < 5) return 'a few seconds ago';
  if (seconds < 60) return `${seconds} seconds ago`;
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  if (hours === 1) return '1 hour ago';
  if (hours < 24) return `${hours} hours ago`;
  if (days === 1) return '1 day ago';
  if (days < 7) return `${days} days ago`;
  if (weeks === 1) return 'a week ago';
  return `${weeks} weeks ago`;
};

export const formatTime = (date: Date): string => {
  return date.toLocaleTimeString();
};

/**
 * Format epoch seconds into a friendly, casual local time string.
 * - Today: "Today at 3:30 PM" / "Tonight at 8:00 PM"
 * - Tomorrow: "Tomorrow at 3:30 PM"
 * - Yesterday: "Yesterday at 3:30 PM"
 * - Within next 7 days: "Mon at 3:30 PM"
 * - Otherwise: "Mar 2, 2026 at 3:30 PM"
 *
 * Mirrors the engine `formatFriendlyDateTime` helper so frontend can render
 * schedule times consistently without depending on a bundled engine export.
 */
export const formatFriendlyDateTime = (epochSeconds: number, now: Date = new Date()): string => {
  if (!Number.isFinite(epochSeconds) || epochSeconds <= 0) {
    return '';
  }

  const date = new Date(epochSeconds * 1000);
  const timeText = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = startOfDay(now);
  const target = startOfDay(date);
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000);

  if (diffDays === 0) {
    const hour = date.getHours();
    if (hour >= 18) {
      return `Tonight at ${timeText}`;
    }
    return `Today at ${timeText}`;
  }

  if (diffDays === 1) {
    return `Tomorrow at ${timeText}`;
  }

  if (diffDays === -1) {
    return `Yesterday at ${timeText}`;
  }

  if (diffDays > 1 && diffDays < 7) {
    const dayName = date.toLocaleDateString(undefined, { weekday: 'short' });
    return `${dayName} at ${timeText}`;
  }

  const dateText = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return `${dateText} at ${timeText}`;
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): T => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return ((...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  }) as T;
};

export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): T => {
  let lastCall = 0;
  return ((...args: any[]) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      return func(...args);
    }
  }) as T;
};
